import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ArrowLeft, Building2, Eye, FileText, LucideAngularModule, Pencil, Plus, ShieldCheck, Trash2, User } from 'lucide-angular';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { VendorsService } from '../services/vendors.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { VendorDocumentsService } from '../../vendor-document/services/vendor-documents.service';
import { DocumentConfig, Vendor, VendorDocument, VendorSavePayload, VendorUserCreatePayload } from '../../../core/models/domain.models';
import { AuthService } from '../../../core/services/auth.service';
import { VENDOR_TYPES } from './vendor.constants';

const GENERATOR_TYPES = ['Solar', 'Thermal', 'Wind', 'Biomass', 'Hydro', 'Bess With Solar', 'Bess Without Solar'];
const VOLTAGE_LEVELS = ['11 kV', '33 kV', '66 kV', '110 kV', '132 kV', '220 kV', '400 kV', '765 kV'];
const STATE_REGION_MAP: Record<string, string> = {
  'Jammu and Kashmir': 'Northern',
  Ladakh: 'Northern',
  'Himachal Pradesh': 'Northern',
  Punjab: 'Northern',
  Uttarakhand: 'Northern',
  Haryana: 'Northern',
  Delhi: 'Northern',
  'Uttar Pradesh': 'Northern',
  Rajasthan: 'Northern',
  Chandigarh: 'Northern',
  Gujarat: 'Western',
  Maharashtra: 'Western',
  Goa: 'Western',
  'Madhya Pradesh': 'Western',
  Chhattisgarh: 'Western',
  Bihar: 'Eastern',
  Jharkhand: 'Eastern',
  Odisha: 'Eastern',
  'West Bengal': 'Eastern',
  Sikkim: 'Eastern',
  'Andhra Pradesh': 'Southern',
  Karnataka: 'Southern',
  Kerala: 'Southern',
  'Tamil Nadu': 'Southern',
  Telangana: 'Southern',
  Puducherry: 'Southern',
  Assam: 'North-Eastern',
  'Arunachal Pradesh': 'North-Eastern',
  Manipur: 'North-Eastern',
  Meghalaya: 'North-Eastern',
  Mizoram: 'North-Eastern',
  Nagaland: 'North-Eastern',
  Tripura: 'North-Eastern',
};
const INDIAN_STATES = Object.keys(STATE_REGION_MAP).sort();

type VendorUserFormRow = {
  id: string;
  name: string;
  username: string;
  department: string;
  designation: string;
  email: string;
  contactNumber: string;
  password?: string;
  isPrimary: boolean;
};

@Component({
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: './vendor-form.page.html',
  styleUrl: './vendor-form.page.scss',
})
export class VendorFormPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly vendorsService = inject(VendorsService);
  private readonly documentsService = inject(VendorDocumentsService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  protected readonly ArrowLeftIcon = ArrowLeft;
  protected readonly Building2Icon = Building2;
  protected readonly UserIcon = User;
  protected readonly FileTextIcon = FileText;
  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly ShieldCheckIcon = ShieldCheck;
  protected readonly Trash2Icon = Trash2;
  protected readonly EyeIcon = Eye;

  protected readonly vendorTypeOptions = VENDOR_TYPES;
  protected readonly generatorTypeOptions = GENERATOR_TYPES;
  protected readonly voltageLevelOptions = VOLTAGE_LEVELS;
  protected readonly stateOptions = INDIAN_STATES;

  protected readonly isEdit = signal(false);
  protected readonly loading = signal(false);
  protected readonly users = signal<VendorUserFormRow[]>([]);
  protected readonly documentConfigs = signal<DocumentConfig[]>([]);
  protected readonly vendorDocuments = signal<VendorDocument[]>([]);
  protected readonly pendingFilesByConfig = signal<Record<string, File | null>>({});
  protected readonly selectedVendorType = signal('');
  protected readonly showUserForm = signal(false);
  protected readonly editingUserId = signal<string | null>(null);
  protected readonly checkingNoarId = signal(false);
  protected readonly noarIdDuplicateError = signal('');
  protected readonly checkingUsername = signal(false);
  protected readonly usernameDuplicateError = signal('');
  private readonly loadedStatus = signal('');
  private vendorId: string | null = null;

  protected readonly form = this.fb.nonNullable.group({
    vendor_name: ['', Validators.required],
    vendor_type: ['', Validators.required],
    noar_id: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    contact_number: [''],
    gst_number: [''],
    pan_number: [''],
    cin: [''],
    exchange_portfolio_id: [''],
    exchange_registered: [false],
    generator_type: [''],
    voltage_level: [''],
    connected_with_ctu: [false],
    discom: [''],
    state: [''],
    region: [''],
    billing_address: [''],
    registered_address: [''],
    status: ['pending'],
    active: [true],
  });
  protected readonly userForm = this.fb.nonNullable.group({
    name: ['', Validators.required],
    username: ['', Validators.required],
    department: ['', Validators.required],
    designation: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    contactNumber: ['', Validators.required],
    password: [''],
    isPrimary: [false],
  });
  protected readonly requiredVendorConfigs = computed(() => {
    const vendorType = this.normalizeDocToken(this.selectedVendorType());
    return this.documentConfigs().filter((cfg) => {
      const moduleName = (cfg.module_name || '').trim().toUpperCase();
      const status = (cfg.status || 'Active').trim().toUpperCase();
      if (status !== 'ACTIVE') return false;
      if (moduleName !== 'VENDOR MODULE' && moduleName !== 'VENDOR') return false;
      const configuredTypes = this.parseClientTypes(cfg.client_types || cfg.client_type || '');
      if (!configuredTypes.length) return true;
      return configuredTypes.some((token) => this.normalizeDocToken(token) === vendorType);
    });
  });
  protected readonly uploadedDocMap = computed(() => {
    const mapByType = new Map<string, VendorDocument>();
    const sorted = [...this.vendorDocuments()].sort((a, b) => {
      const left = new Date(a.uploaded_at || 0).getTime();
      const right = new Date(b.uploaded_at || 0).getTime();
      return right - left;
    });
    sorted.forEach((item) => {
      const token = this.normalizeDocToken(item.document_type);
      if (token && !mapByType.has(token)) mapByType.set(token, item);
    });
    return mapByType;
  });
  protected readonly missingMandatoryConfigs = computed(() =>
    this.requiredVendorConfigs().filter((cfg) => this.isConfigMandatory(cfg) && !this.hasDocumentReady(cfg)),
  );
  protected readonly isVendorApproved = computed(() => {
    const source = this.isEdit() ? this.loadedStatus() || this.form.controls.status.value || '' : this.form.controls.status.value || '';
    return source.trim().toLowerCase().includes('approve');
  });
  protected readonly userRole = computed(() =>
    (this.auth.user()?.role || '').trim().toLowerCase().replace(/\s+/g, '_'),
  );
  protected readonly isAdminUser = computed(() => {
    const role = this.userRole();
    return role === 'admin' || role === 'super_admin';
  });
  protected readonly canEditExistingVendor = computed(() => !this.isEdit() || this.isAdminUser());
  protected readonly isVendorSaveBlocked = computed(() => {
    const vendorType = this.selectedVendorType().trim();
    return !!vendorType && this.requiredVendorConfigs().length === 0;
  });
  private lastMissingConfigWarningToken = '';

  constructor() {
    this.setupValidationWatchers();
    this.loadDocumentConfigs();
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (this.userRole() === 'vendor') {
        this.toast.show('Vendor profile is read-only', '', 'danger');
        this.router.navigateByUrl(this.auth.user()?.vendor_id ? `/vendors/${this.auth.user()?.vendor_id}` : '/');
        return;
      }
      this.vendorId = id;
      this.isEdit.set(!!id);
      if (id) {
        this.loading.set(true);
        this.vendorsService.get(id).subscribe({
          next: (vendor) => {
            this.loadedStatus.set(String((vendor as any)?.status || ''));
            this.form.patchValue(vendor as any);
            this.selectedVendorType.set(String((vendor as any)?.vendor_type || ''));
            this.notifyMissingVendorConfigsIfNeeded();
            this.loadVendorUsers(id);
            this.loadVendorDocuments(id);
            if (!this.canEditExistingVendor()) {
              this.form.disable({ emitEvent: false });
            }
            this.loading.set(false);
          },
          error: () => {
            this.loading.set(false);
            this.toast.show('Vendor not found', '', 'danger');
            this.router.navigateByUrl('/vendors');
          },
        });
      } else {
        const status = this.defaultStatusForCreator();
        this.loadedStatus.set(status);
        this.form.patchValue({ status });
        this.selectedVendorType.set('');
      }
    });
  }

  protected onVendorTypeChange(): void {
    this.selectedVendorType.set(this.form.controls.vendor_type.value || '');
    if (this.form.controls.vendor_type.value !== 'Generator') {
      this.form.patchValue({ generator_type: '', voltage_level: '', connected_with_ctu: false, discom: '' });
    }
    this.notifyMissingVendorConfigsIfNeeded();
  }

  protected onStateChange(): void {
    const state = this.form.controls.state.value;
    this.form.patchValue({ region: STATE_REGION_MAP[state] || '' });
  }

  protected onCtuChange(): void {
    if (this.form.controls.connected_with_ctu.value) {
      this.form.patchValue({ discom: '' });
    }
  }

  protected submit(): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    if (this.isVendorSaveBlocked()) {
      this.toast.show('Please add active documents from Master document configuration for selected vendor type.', '', 'danger');
      return;
    }
    if (this.checkingNoarId()) return;
    if (this.noarIdDuplicateError()) {
      this.form.controls.noar_id.markAsTouched();
      return;
    }
    if (this.form.invalid) return;
    if ((this.form.controls.status.value || '').trim().toLowerCase() === 'approved' && this.missingMandatoryConfigs().length) {
      this.toast.show('Upload mandatory documents before approving vendor', '', 'danger');
      return;
    }
    this.loading.set(true);
    const payload = this.form.getRawValue();
    if (!this.vendorId) {
      payload.status = this.defaultStatusForCreator();
    }
    const savePayload: VendorSavePayload = {
      ...payload,
      noar_id: payload.noar_id || '',
      email: payload.email || '',
      vendor_name: payload.vendor_name || '',
      vendor_type: payload.vendor_type || '',
      users: this.toUserPayload(),
    };

    this.vendorsService
      .save(savePayload, this.vendorId || undefined)
      .pipe(
        switchMap((vendor: Vendor) => this.submitPendingDocuments(vendor.id).pipe(map(() => vendor))),
      )
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.toast.show(this.vendorId ? 'Vendor updated' : 'Vendor created', '', 'success');
          this.router.navigateByUrl('/vendors');
        },
        error: (error) => {
          this.loading.set(false);
          const detail = String(error?.error?.detail || '');
          if (detail.toLowerCase().includes('duplicate noar id')) {
            this.setDuplicateError(this.form.controls.noar_id, 'duplicate', true);
            this.noarIdDuplicateError.set('duplicate NOAR ID');
          }
          if (this.handleVendorUsernameConflict(detail)) {
            this.toast.show(detail, '', 'danger');
            return;
          }
          this.toast.show(detail || 'Failed to save vendor', '', 'danger');
        },
      });
  }

  protected docTitle(cfg: DocumentConfig): string {
    return (cfg.doc_title || cfg.document_name || '').trim();
  }

  protected isConfigMandatory(cfg: DocumentConfig): boolean {
    return !!(cfg.is_mandatory ?? cfg.mandatory);
  }

  protected displayAllowedTypes(cfg: DocumentConfig): string {
    return (cfg.allowed_file_types || cfg.file_type || 'pdf').toLowerCase();
  }

  protected maxFileSizeMb(cfg: DocumentConfig): number {
    const value = Number(cfg.max_file_size_mb ?? cfg.max_file_size ?? 10);
    if (!Number.isFinite(value) || value <= 0) return 10;
    return value;
  }

  protected acceptByConfig(cfg: DocumentConfig): string {
    return this.allowedExtensions(cfg).map((ext) => `.${ext}`).join(',');
  }

  protected pendingFile(cfg: DocumentConfig): File | null {
    return this.pendingFilesByConfig()[cfg.id] || null;
  }

  protected displayDocName(cfg: DocumentConfig): string {
    return this.pendingFile(cfg)?.name || this.docForConfig(cfg)?.original_name || '';
  }

  protected docForConfig(cfg: DocumentConfig): VendorDocument | null {
    return this.uploadedDocMap().get(this.normalizeDocToken(this.docTitle(cfg))) || null;
  }

  protected triggerConfigFileInput(input: HTMLInputElement): void {
    if (!this.canEditExistingVendor()) return;
    input.click();
  }

  protected onConfigFileSelected(cfg: DocumentConfig, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = (input.files || [])[0] || null;
    if (!file) return;
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      input.value = '';
      return;
    }
    const docType = this.docTitle(cfg);
    if (!docType) {
      this.toast.show('Invalid document configuration', '', 'danger');
      input.value = '';
      return;
    }
    const allowed = this.allowedExtensions(cfg);
    const ext = this.fileExtension(file.name);
    if (allowed.length && (!ext || !allowed.includes(ext))) {
      this.toast.show(`Invalid file type. Allowed: ${allowed.join(', ')}`, '', 'danger');
      input.value = '';
      return;
    }
    const maxBytes = this.maxFileSizeMb(cfg) * 1024 * 1024;
    if (file.size > maxBytes) {
      this.toast.show(`File exceeds max size ${this.maxFileSizeMb(cfg)} MB`, '', 'danger');
      input.value = '';
      return;
    }

    this.pendingFilesByConfig.set({ ...this.pendingFilesByConfig(), [cfg.id]: file });
    this.toast.show(`${docType} selected. It will upload on submit.`, '', 'info');
    input.value = '';
  }

  protected viewConfigDocument(cfg: DocumentConfig): void {
    const pending = this.pendingFile(cfg);
    if (pending) {
      const url = URL.createObjectURL(pending);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return;
    }
    const doc = this.docForConfig(cfg);
    if (!doc) return;
    window.open(this.documentsService.downloadUrl(doc.id), '_blank');
  }

  protected deleteConfigDocument(cfg: DocumentConfig): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    if (this.isDocumentLocked()) {
      this.toast.show('Vendor is approved. Delete is not allowed.', '', 'danger');
      return;
    }
    const pending = this.pendingFile(cfg);
    if (pending) {
      const current = { ...this.pendingFilesByConfig() };
      current[cfg.id] = null;
      this.pendingFilesByConfig.set(current);
      this.toast.show('Selected file removed', '', 'success');
      return;
    }
    const doc = this.docForConfig(cfg);
    if (!doc) return;
    if (!confirm(`Delete ${doc.original_name}?`)) return;
    this.documentsService.deleteDocument(doc.id).subscribe({
      next: () => {
        this.toast.show('Document deleted', '', 'success');
        if (this.vendorId) this.loadVendorDocuments(this.vendorId);
      },
      error: () => this.toast.show('Failed to delete document', '', 'danger'),
    });
  }

  protected approveVendor(): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    if (!this.vendorId) return;
    if (this.missingMandatoryConfigs().length) {
      this.toast.show('Upload all mandatory documents before approval', '', 'danger');
      return;
    }
    this.loading.set(true);
    this.submitPendingDocuments(this.vendorId)
      .pipe(switchMap(() => this.vendorsService.update(this.vendorId || '', { status: 'approved' })))
      .subscribe({
        next: (vendor) => {
          this.loading.set(false);
          this.loadedStatus.set(String(vendor.status || 'approved'));
          this.form.patchValue({ status: vendor.status || 'approved' });
          this.toast.show('Vendor approved', '', 'success');
        },
        error: () => {
          this.loading.set(false);
          this.toast.show('Failed to approve vendor', '', 'danger');
        },
      });
  }

  protected openAddUser(): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    this.editingUserId.set(null);
      this.clearUsernameDuplicateState();
      this.userForm.reset({
        name: '',
        username: '',
        department: '',
        designation: '',
        email: '',
        contactNumber: '',
        password: '',
        isPrimary: false,
      });
    this.showUserForm.set(true);
  }

  protected editUser(user: VendorUserFormRow): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    this.editingUserId.set(user.id);
    this.clearUsernameDuplicateState();
    this.userForm.reset({
      name: user.name,
      username: user.username,
      department: user.department,
      designation: user.designation,
      email: user.email,
      contactNumber: user.contactNumber,
      password: '',
      isPrimary: user.isPrimary,
    });
    this.showUserForm.set(true);
  }

  protected closeUserForm(): void {
    this.showUserForm.set(false);
    this.clearUsernameDuplicateState();
    this.userForm.markAsPristine();
    this.userForm.markAsUntouched();
  }

  protected saveUser(): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    if (this.checkingUsername()) return;
    if (this.usernameDuplicateError()) {
      this.userForm.controls.username.markAsTouched();
      return;
    }

    const value = this.userForm.getRawValue();
    const userId = this.editingUserId();
    if (!userId && !value.password.trim()) {
      this.toast.show('Password is required for a new vendor user', '', 'danger');
      return;
    }
    const shouldBePrimary = value.isPrimary;

    if (userId) {
      this.users.update((current) =>
        current.map((item) =>
          item.id === userId
            ? { ...item, ...value }
            : shouldBePrimary
              ? { ...item, isPrimary: false }
              : item,
        ),
      );
      this.toast.show('User updated', '', 'success');
    } else {
      const newUser: VendorUserFormRow = {
        id: `${Date.now()}`,
        ...value,
      };
      this.users.update((current) => {
        const normalized = shouldBePrimary ? current.map((item) => ({ ...item, isPrimary: false })) : current;
        return [...normalized, newUser];
      });
      this.toast.show('User added', '', 'success');
    }

    this.closeUserForm();
  }

  protected removeUser(user: VendorUserFormRow): void {
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor', '', 'danger');
      return;
    }
    if (!confirm(`Delete user ${user.name}?`)) return;
    this.users.update((current) => current.filter((item) => item.id !== user.id));
    this.toast.show('User deleted', '', 'success');
  }

  protected openUserPrivileges(user: VendorUserFormRow): void {
    if (!this.vendorId) {
      this.toast.show('Save the vendor first to manage user privileges', '', 'danger');
      return;
    }
    if (!this.canEditExistingVendor()) {
      this.toast.show('Only admin can update vendor user privileges', '', 'danger');
      return;
    }
    void this.router.navigate(['/vendors', this.vendorId, 'users', user.id, 'privileges']);
  }

  private loadVendorUsers(vendorId: string): void {
    this.vendorsService.listUsers(vendorId).subscribe({
      next: (users) =>
        this.users.set(
          (users || []).map((user) => ({
            id: user.id,
            name: user.name,
            username: user.username,
            department: user.department,
            designation: user.designation,
            email: user.email,
            contactNumber: user.contact_number || '',
            isPrimary: !!user.is_primary,
          })),
        ),
      error: () => this.users.set([]),
    });
  }

  private loadDocumentConfigs(): void {
    this.documentsService.listConfigs().subscribe({
      next: (configs) => {
        this.documentConfigs.set(configs || []);
        this.notifyMissingVendorConfigsIfNeeded();
      },
      error: () => {
        this.documentConfigs.set([]);
        this.notifyMissingVendorConfigsIfNeeded();
      },
    });
  }

  private loadVendorDocuments(vendorId: string): void {
    this.documentsService.listVendorDocuments(vendorId).subscribe({
      next: (docs) => this.vendorDocuments.set(docs || []),
      error: () => this.vendorDocuments.set([]),
    });
  }

  private submitPendingDocuments(vendorId: string): Observable<unknown> {
    const tasks: Observable<unknown>[] = [];
    for (const cfg of this.requiredVendorConfigs()) {
      const file = this.pendingFilesByConfig()[cfg.id];
      if (!file) continue;
      const docType = this.docTitle(cfg);
      if (!docType) continue;
      const existing = this.docForConfig(cfg);
      const task$ = existing
        ? this.documentsService
            .deleteDocument(existing.id)
            .pipe(switchMap(() => this.documentsService.uploadVendorDocuments(vendorId, docType, [file])))
        : this.documentsService.uploadVendorDocuments(vendorId, docType, [file]);
      tasks.push(task$);
    }
    if (!tasks.length) return of(null);
    return forkJoin(tasks).pipe(
      map(() => {
        this.pendingFilesByConfig.set({});
        this.loadVendorDocuments(vendorId);
      }),
    );
  }

  private parseClientTypes(value: string): string[] {
    const text = (value || '').trim();
    if (!text) return [];
    const normalized = text.startsWith('[') ? text.replace(/^\[|\]$/g, '') : text;
    return normalized
      .split(',')
      .map((item) => item.replace(/['"]/g, '').trim())
      .filter(Boolean);
  }

  private normalizeDocToken(value: string): string {
    return (value || '').trim().replace(/\s+/g, ' ').toUpperCase();
  }

  private allowedExtensions(cfg: DocumentConfig): string[] {
    return this.displayAllowedTypes(cfg)
      .split(',')
      .map((item) => item.trim().toLowerCase().replace(/^\./, ''))
      .filter(Boolean);
  }

  private fileExtension(name: string): string {
    const parts = (name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  private defaultStatusForCreator(): string {
    const role = (this.auth.user()?.role || '').trim().toLowerCase().replace(/\s+/g, '_');
    return role === 'admin' || role === 'super_admin' ? 'approved' : 'pending';
  }

  private hasDocumentReady(cfg: DocumentConfig): boolean {
    return !!this.docForConfig(cfg) || !!this.pendingFile(cfg);
  }

  private notifyMissingVendorConfigsIfNeeded(): void {
    const vendorType = this.selectedVendorType().trim();
    if (!vendorType) {
      this.lastMissingConfigWarningToken = '';
      return;
    }
    if (this.requiredVendorConfigs().length > 0) {
      this.lastMissingConfigWarningToken = '';
      return;
    }

    const token = this.normalizeDocToken(vendorType);
    if (this.lastMissingConfigWarningToken === token) return;
    this.lastMissingConfigWarningToken = token;
    this.toast.show('Please add active documents from Master document configuration for selected vendor type.', '', 'danger');
  }

  protected isDocumentLocked(): boolean {
    return this.isEdit() && this.isVendorApproved();
  }

  private toUserPayload(): VendorUserCreatePayload[] {
    return this.users().map((user) => ({
      id: user.id,
      name: user.name,
      username: user.username,
      department: user.department,
      designation: user.designation,
      email: user.email,
      contact_number: user.contactNumber || null,
      password: user.password?.trim() || null,
      is_primary: !!user.isPrimary,
      status: 'active',
    }));
  }

  private setupValidationWatchers(): void {
    this.form.controls.noar_id.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((value) => (value || '').trim()),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          this.checkingNoarId.set(false);
          if (!value) {
            this.noarIdDuplicateError.set('');
            this.clearDuplicateError(this.form.controls.noar_id, 'duplicate');
            return of(true);
          }
          this.checkingNoarId.set(true);
          return this.vendorsService.validateNoarId(value, this.vendorId || undefined).pipe(
            map((response) => response.available),
            catchError(() => of(true)),
          );
        }),
      )
      .subscribe((available) => {
        this.checkingNoarId.set(false);
        if (available) {
          this.noarIdDuplicateError.set('');
          this.clearDuplicateError(this.form.controls.noar_id, 'duplicate');
          return;
        }
        this.noarIdDuplicateError.set('duplicate NOAR ID');
        this.setDuplicateError(this.form.controls.noar_id, 'duplicate', true);
      });

    this.userForm.controls.username.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((value) => (value || '').trim()),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((value) => {
          this.checkingUsername.set(false);
          if (!value || !this.showUserForm()) {
            this.clearUsernameDuplicateState();
            return of(true);
          }
          const normalized = this.normalizeIdentity(value);
          const editingId = this.editingUserId();
          const localDuplicate = this.users().some(
            (item) => this.normalizeIdentity(item.username) === normalized && item.id !== editingId,
          );
          if (localDuplicate) return of(false);
          this.checkingUsername.set(true);
          return this.vendorsService
            .validateVendorUsername(value, this.vendorId || undefined, editingId || undefined)
            .pipe(
              map((response) => response.available),
              catchError(() => of(true)),
            );
        }),
      )
      .subscribe((available) => {
        this.checkingUsername.set(false);
        if (available) {
          this.clearUsernameDuplicateState();
          return;
        }
        this.usernameDuplicateError.set('duplicate username');
        this.setDuplicateError(this.userForm.controls.username, 'duplicate', true);
      });
  }

  private normalizeIdentity(value: string | null | undefined): string {
    return (value || '').trim().toLowerCase();
  }

  private clearUsernameDuplicateState(): void {
    this.checkingUsername.set(false);
    this.usernameDuplicateError.set('');
    this.clearDuplicateError(this.userForm.controls.username, 'duplicate');
  }

  private handleVendorUsernameConflict(detail: string): boolean {
    const normalized = detail.trim().toLowerCase();
    const isUsernameConflict =
      normalized.includes('vendor user login already exists for another vendor:') ||
      normalized.includes('duplicate vendor username:') ||
      normalized.includes('vendor user login conflicts with an existing staff account:');
    if (!isUsernameConflict) return false;

    const username = detail.split(':').slice(1).join(':').trim();
    if (username) {
      const matchedUser = this.users().find((item) => this.normalizeIdentity(item.username) === this.normalizeIdentity(username));
      if (matchedUser) {
        this.editUser(matchedUser);
      } else {
        this.showUserForm.set(true);
      }
      this.userForm.controls.username.setValue(username, { emitEvent: false });
    } else {
      this.showUserForm.set(true);
    }

    this.usernameDuplicateError.set('duplicate username');
    this.setDuplicateError(this.userForm.controls.username, 'duplicate', true);
    this.userForm.controls.username.markAsTouched();
    return true;
  }

  private setDuplicateError(control: { errors: Record<string, unknown> | null; setErrors: (errors: Record<string, unknown> | null) => void }, key: string, value: true): void {
    control.setErrors({ ...(control.errors || {}), [key]: value });
  }

  private clearDuplicateError(control: { errors: Record<string, unknown> | null; setErrors: (errors: Record<string, unknown> | null) => void }, key: string): void {
    const current = { ...(control.errors || {}) };
    delete current[key];
    control.setErrors(Object.keys(current).length ? current : null);
  }
}
